import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Shield, ShieldAlert, KeyRound, LogIn, LogOut, UserPlus, Ban } from "lucide-react";

const ACTION_META = {
  login_success:        { label: "Login OK",           icon: LogIn,       tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  login_failure:        { label: "Login failed",       icon: ShieldAlert, tone: "bg-amber-100 text-amber-800 border-amber-200" },
  login_locked:         { label: "Locked out",         icon: Ban,         tone: "bg-rose-100 text-rose-800 border-rose-200" },
  "2fa_verify_success": { label: "2FA OK",             icon: Shield,      tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  "2fa_verify_failure": { label: "2FA failed",         icon: ShieldAlert, tone: "bg-amber-100 text-amber-800 border-amber-200" },
  logout:               { label: "Logout",             icon: LogOut,      tone: "bg-slate-100 text-slate-700 border-slate-200" },
  password_changed:     { label: "Password changed",   icon: KeyRound,    tone: "bg-sky-100 text-sky-800 border-sky-200" },
  "2fa_enabled":        { label: "2FA enabled",        icon: Shield,      tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  "2fa_disabled":       { label: "2FA disabled",       icon: ShieldAlert, tone: "bg-rose-100 text-rose-800 border-rose-200" },
  invite_accepted:      { label: "Invite accepted",    icon: UserPlus,    tone: "bg-violet-100 text-violet-800 border-violet-200" },
};

const ALL_ACTIONS = Object.keys(ACTION_META);

export default function AuthEventsPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [emailFilter, setEmailFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      params.set("page", page);
      params.set("page_size", "50");
      if (emailFilter.trim()) params.set("user_email", emailFilter.trim());
      if (actionFilter !== "all") params.set("action", actionFilter);
      const [{ data }, { data: sum }] = await Promise.all([
        api.get(`/audit/auth-events?${params.toString()}`),
        api.get(`/audit/auth-events/summary`),
      ]);
      setItems(data.items || []); setTotal(data.total || 0); setSummary(sum);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [emailFilter, actionFilter, page]);

  return (
    <div data-testid="auth-events-panel">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {["login_success","login_failure","login_locked","2fa_verify_failure","password_changed"].map((k) => {
          const meta = ACTION_META[k];
          const n = summary?.last_24h?.[k] ?? 0;
          const Icon = meta.icon;
          return (
            <div key={k} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-500">
                <Icon className="w-3.5 h-3.5" /> {meta.label}
              </div>
              <div className="mt-1 font-display text-2xl text-slate-900">{n}</div>
              <div className="text-[10px] text-slate-400">last 24h · {summary?.last_7d?.[k] ?? 0} in 7d</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <Input
          data-testid="auth-events-email-filter"
          value={emailFilter}
          onChange={(e) => { setEmailFilter(e.target.value); setPage(1); }}
          placeholder="Filter by email (exact match)"
          className="max-w-xs bg-white"
        />
        <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(1); }}>
          <SelectTrigger data-testid="auth-events-action-filter" className="max-w-[220px] bg-white"><SelectValue placeholder="All actions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {ALL_ACTIONS.map((a) => <SelectItem key={a} value={a}>{ACTION_META[a].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-500 ml-auto">Total: {total} · Page {page}</div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[11px] uppercase tracking-wider text-white bg-gbe">
          <div className="col-span-3">When</div>
          <div className="col-span-3">User</div>
          <div className="col-span-2">Action</div>
          <div className="col-span-2">IP</div>
          <div className="col-span-2">Client</div>
        </div>
        <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
          {busy && <div className="p-10 text-center text-sm text-slate-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading…</div>}
          {!busy && items.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-500">No authentication events match this filter.</div>
          )}
          {items.map((e) => {
            const meta = ACTION_META[e.action] || { label: e.action, tone: "bg-slate-100 text-slate-700", icon: Shield };
            const Icon = meta.icon;
            return (
              <div key={e.id} className="grid grid-cols-12 gap-2 px-4 py-2.5 text-sm text-slate-700 items-center">
                <div className="col-span-3 text-xs text-slate-500 font-mono">{e.timestamp?.slice(0, 19).replace("T", " ")}</div>
                <div className="col-span-3 truncate">{e.user_email || <span className="text-slate-400">—</span>}</div>
                <div className="col-span-2">
                  <Badge className={`${meta.tone} border font-normal`}>
                    <Icon className="w-3 h-3 mr-1" /> {meta.label}
                  </Badge>
                </div>
                <div className="col-span-2 text-xs text-slate-500 font-mono">{e.ip || "—"}</div>
                <div className="col-span-2 text-xs text-slate-500 truncate" title={e.user_agent}>
                  {(e.user_agent || "").split(" ")[0] || "—"}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pagination */}
      {total > 50 && (
        <div className="flex items-center justify-end gap-2 mt-3">
          <button className="px-3 py-1 text-xs rounded border border-slate-200 disabled:opacity-40" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
          <span className="text-xs text-slate-500">Page {page} of {Math.ceil(total / 50)}</span>
          <button className="px-3 py-1 text-xs rounded border border-slate-200 disabled:opacity-40" disabled={page >= Math.ceil(total / 50)} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
